// Object URLs for pictures fetched behind the app's token (an <img> cannot send
// it): blob pictures in Prime's threads (SessionPictures) and a coding agent's
// pictures (RunPictures). A picture never changes, so one fetch per key is
// kept and shared. Each shown picture holds its URL while it is mounted; a URL
// is revoked only once nothing holds it and the cache is past its cap, so a
// thread with more pictures than the cap never loses one it is showing (they
// load lazily, long after the fetch). Pure apart from the URL functions, which
// are passed in for tests.

export interface BlobUrls {
  /** Hold the URL for key, fetching it with load the first time. Call release when done. */
  acquire(key: string, load: () => Promise<Blob>): { url: Promise<string>; release: () => void }
  /** Forget key's URL (it failed to draw): revoke it now, so the next acquire fetches afresh. */
  drop(key: string): void
  /** How many URLs are kept (for tests). */
  size(): number
}

interface Entry {
  url: Promise<string>
  holders: number
}

export const createBlobUrls = (
  max: number,
  make: (b: Blob) => string = (b) => URL.createObjectURL(b),
  revoke: (u: string) => void = (u) => URL.revokeObjectURL(u),
): BlobUrls => {
  // insertion order is recency: most recently acquired last
  const cache = new Map<string, Entry>()
  const revokeOf = (e: Entry) =>
    e.url.then(
      (u) => revoke(u),
      () => {},
    )
  // past the cap, let go of the least recently used URLs nothing holds
  const trim = () => {
    if (cache.size <= max) return
    for (const [key, e] of cache) {
      if (cache.size <= max) break
      if (e.holders > 0) continue
      cache.delete(key)
      void revokeOf(e)
    }
  }
  return {
    acquire(key, load) {
      let e = cache.get(key)
      if (e) {
        cache.delete(key)
        cache.set(key, e)
      } else {
        const fresh: Entry = { url: load().then(make), holders: 0 }
        // a failed fetch is not remembered, so the next look tries again
        fresh.url.catch(() => {
          if (cache.get(key) === fresh) cache.delete(key)
        })
        cache.set(key, fresh)
        e = fresh
      }
      const held = e
      held.holders++
      let released = false
      trim()
      return {
        url: held.url,
        release: () => {
          if (released) return
          released = true
          held.holders--
          // an entry dropped while held has been revoked already; one still kept may go now
          if (cache.get(key) === held) trim()
        },
      }
    },
    drop(key) {
      const e = cache.get(key)
      if (!e) return
      cache.delete(key)
      void revokeOf(e)
    },
    size: () => cache.size,
  }
}
